import {
  APIListValuesRequest,
  APIListValuesResponse,
  APIPaginatedRequest,
  APISuccessResponse,
  AssociatedVehicleTimelineRequest,
  Attachment,
  ByVehicleAgeAgeRequest,
  Dealer,
  ExportRequest,
  getFetcher,
  LaborCode,
  ListAttributesRequest,
  ListEventsRequest,
  Part,
  SmartGroup,
  TopContributorsAgeRequest,
} from "shared/api/api";
import client from "shared/api/axios";
import { Granularity } from "shared/api/constants";
import {
  SignalEvent,
  SignalEventsOccurrence,
} from "shared/api/signalEvents/api";
import { Photo } from "shared/api/types";
import { createURL } from "shared/api/utils";
import {
  CountRequestWithVehiclesFilter,
  ListVehiclesRequest,
  Vehicle,
} from "shared/api/vehicles/api";
import { MileageUnit } from "shared/types";

const CLAIMS_BASE_ROUTE = "claims";
const PHOTOS_BASE_ROUTE = "photos";

export interface Claim {
  ID: string;
  VIN: string;
  AISummary: string | null;
  campaignID: string | null;
  costLabor: number | null;
  costParts: number | null;
  costTotal: number | null;
  costOther: number | null;
  totalApprovedNetCost: number | null;
  createdAt: string;
  currencyCode: string | null;
  data: Record<string, string | undefined>;
  date: string;
  dealer: Dealer | null;
  dealerID: string | null;
  externalID: string | null;
  externalURL: string | null;
  failedPartNumber: string | null;
  laborCode: string | null;
  mileage: number | null;
  mileageUnit: MileageUnit | null;
  notes: string | null;
  parts: string[] | null;
  repairOrderNumber: string | null;
  status: string | null;
  updatedAt: string;
  binComponent: string | null;
  binSymptom: string | null;
  binProblem: string | null;
  binLocation: string | null;
  binResponsibility: string | null;
  complaintDetail: string | null;
  componentDetail: string | null;
  causalDetail: string | null;
  repairDetail: string | null;
  causalCode: string | null;
  binIssue: string | null;
  notesTechnician: string | null;
  notesPart: string | null;
  notesCorrection: string | null;
  notesCustomer: string | null;
  group: string | null;
  subgroup: string | null;
  transactionCategory: string | null;
  monthsInService: number | null;
  daysInService: number | null;
  mentionedPhrases: string[] | null;
  mentionedRootCauses: string[] | null;
  mentionedSymptoms: string[] | null;
  mentionedSignalEvents: SignalEvent[] | null;
  failedPartInformation: Part | null;
  laborCodeInformation: LaborCode | null;
  vehicle?: Vehicle;
  // added so they can be included in the schema that does keys of Claim
  "failedPartInformation.description": string | null;
  "laborCodeInformation.description": string | null;
  nonCausalParts: Part[] | null;
  matchedEventCounts?: { [key: string]: number };
  photos?: Photo[];
  vehiclePhotos?: Photo[];
  smartGroups?: SmartGroup[];
  matchedClaimsCount?: number;
  attachments: Attachment[];
  vehicleAttachments?: Attachment[];
}

export interface ClaimSmartGroup {
  groupID: string;
  groupName: string;
  groupDescription: string;
  filteredCount: number;
  allTimeCount: number;
  filteredVehicleCount: number;
  allTimeVehicleCount: number;
  filteredFRCImpact: number;
  allTimeFRCImpact: number;
  groupExternalID: string;
  totalGroupCount?: number;
}

export const listClaimsRequestURI = ({
  skipRequest,
  ...params
}: ListEventsRequest): string =>
  skipRequest
    ? ""
    : client.getUri({
        method: "GET",
        url: CLAIMS_BASE_ROUTE,
        params,
      });

export const listClaimsSmartGroupsRequestURI = (
  params: ListClaimsSmartGroupsRequest
): string =>
  client.getUri({
    method: "GET",
    url: createURL([CLAIMS_BASE_ROUTE, "smartGroups"]),
    params,
  });

export const listClaims = (
  args: ListEventsRequest
): Promise<APISuccessResponse<Claim[]>> =>
  getFetcher(
    listClaimsRequestURI({
      ...args,
      analytics: args.analytics != null ? args.analytics : true,
    })
  ); // always include analytics for listing if not explicitly set to false

export const listClaimsCountRequestURI = ({
  ...params
}: CountRequestWithVehiclesFilter): string =>
  client.getUri({
    method: "GET",
    url: createURL([CLAIMS_BASE_ROUTE, "count"]),
    params,
  });

interface APIListClaimValuesRequest extends APIListValuesRequest {
  analytics?: boolean;
}

export const listClaimsValues = (args: APIListClaimValuesRequest) => {
  args = { ...args, analytics: true };

  return getFetcher<APIListValuesResponse>(listClaimsValuesRequestURI(args));
};

const listClaimsValuesRequestURI = ({
  fieldName,
  ...params
}: APIListClaimValuesRequest): string =>
  client.getUri({
    method: "get",
    url: createURL([CLAIMS_BASE_ROUTE, "values", fieldName]),
    params,
  });

const getClaimsExportRequestURI = ({
  type = "xlsx",
  ...params
}: ExportRequest): string =>
  client.getUri({
    method: "get",
    url: createURL([CLAIMS_BASE_ROUTE, "export", type]),
    params,
  });

export const getClaimsExport = (args: ExportRequest) =>
  getFetcher<Blob>(getClaimsExportRequestURI(args), {
    responseType: "blob",
  });

export interface ClaimFiltersRequest {
  claimsFilter?: string;
  vehiclesFilter?: string;
  signalEventOccurrencesFilter?: string;
}

export interface ClaimsRunChartRequest {
  claimFilter?: string;
  vehiclesFilter?: string;
  groupBy: string | null;
  numSplits?: number;
  granularity?: Granularity;
}

export interface ClaimFiltersByVehicleAgeRequest
  extends ClaimFiltersRequest, ByVehicleAgeAgeRequest {
  hideCosts: boolean;
  limit?: number;
}

export interface ClaimsRunChartResponse {
  data: ClaimsRunChartItem[];
  numTotalSplits: number | null;
}

export interface ClaimsRunChartItem {
  date: string;
  affectedVehicles: number;
  events: number;
  groupByAttributeValue: string | null;
}

export const getClaimsRunChartRequestURI = (
  params: ClaimsRunChartRequest
): string =>
  client.getUri({
    method: "get",
    url: createURL([CLAIMS_BASE_ROUTE, "runChart"]),
    params,
  });

const getPhotoDetailsURI = ({
  photoID,
  includeSrcURLs,
}: {
  photoID: string;
  includeSrcURLs?: boolean;
}): string =>
  client.getUri({
    method: "get",
    url: createURL([PHOTOS_BASE_ROUTE, photoID]),
    params: { includeSrcURLs },
  });

export const getPhotoDetails = async ({
  photoID,
  includeSrcURLs,
}: {
  photoID: string;
  includeSrcURLs?: boolean;
}) => getFetcher<Photo>(getPhotoDetailsURI({ photoID, includeSrcURLs }));

export interface GetClaimRequest {
  ID: string;
  mileageUnit: MileageUnit;
  analytics?: boolean;
  includeSrcURLs?: boolean;
}

export const getClaimRequestURI = ({
  ID,
  ...params
}: GetClaimRequest): string =>
  client.getUri({
    method: "get",
    url: createURL([CLAIMS_BASE_ROUTE, ID]),
    params,
  });

export interface ClaimsTopContributorsRequest
  extends APIPaginatedRequest, ClaimFiltersRequest, TopContributorsAgeRequest {
  groupByAttribute: string;
  hideCosts: boolean;
}

export interface ClaimsTopContributor {
  groupByAttributeValue: string | number | boolean | null;
  IPTV: number;
  IPTV30DayChangeRatio: number | null;
  CPV: number;
  CPV30DayChangeRatio: number | null;
  totalCost: number;
  numClaims: number;
  numEvents: number;
  numVehicles: number;
  totalNumVehicles: number;
  exposureBucket: number | null;
}

export const getClaimsTopContributorsRequestURI = ({
  groupByAttribute,
  ...otherParams
}: ClaimsTopContributorsRequest): string =>
  client.getUri({
    method: "get",
    url: createURL([CLAIMS_BASE_ROUTE, "topContributors", groupByAttribute]),
    params: otherParams,
  });

export const listClaimAttributesRequestURI = (
  params: ListAttributesRequest
): string =>
  client.getUri({
    method: "get",
    url: createURL([CLAIMS_BASE_ROUTE, "attributes"]),
    params,
  });

const getClaimsTopContributorsExportRequestURI = ({
  IDs,
  type = "xlsx",
  ...params
}: ExportRequest): string =>
  client.getUri({
    method: "get",
    url: createURL([
      CLAIMS_BASE_ROUTE,
      "topContributors",
      ...IDs,
      "export",
      type,
    ]),
    params,
  });

export const getClaimsTopContributorsExport = (args: ExportRequest) =>
  getFetcher<Blob>(getClaimsTopContributorsExportRequestURI(args), {
    responseType: "blob",
  });

// By Production Date
export const getClaimsByVehicleAgeTimelineRequestURI = (
  params: ClaimFiltersByVehicleAgeRequest
): string | null => {
  // The API rejects requests without these params, so skip the fetch until they are all set.
  if (
    !params.byVehicleAgeBirthday ||
    !params.byVehicleAgeExposure ||
    !params.granularity
  ) {
    return null;
  }

  return client.getUri({
    method: "get",
    url: createURL([CLAIMS_BASE_ROUTE, "claimsByVehicleAgeTimeline"]),
    params,
  });
};

export interface ClaimAssociatedSignalEvent {
  IPTV: number;
  numAssociatedClaims: number;
  numAssociatedVehicles: number;
  percentClaimsWithPreceding30DaysSignalEvent: number;
  signalEventDescription: string | null;
  signalEventID: string;
  totalEventOccurrences: number;
  associationStrength: number | null;
}

const getClaimsByVehicleAgeTimelineExportRequestURI = ({
  type = "xlsx",
  ...params
}: ExportRequest): string =>
  client.getUri({
    method: "get",
    url: createURL([
      CLAIMS_BASE_ROUTE,
      "claimsByVehicleAgeTimeline",
      "export",
      type,
    ]),
    params,
  });

export const getClaimsByVehicleAgeTimelineExport = (args: ExportRequest) =>
  getFetcher<Blob>(getClaimsByVehicleAgeTimelineExportRequestURI(args), {
    responseType: "blob",
  });

export interface ClaimAssociatedSignalEventsRequest
  extends APIPaginatedRequest, ClaimFiltersRequest {
  signalEventsTimeWindow?: number;
}

export const listAssociatedSignalEventsRequestURI = (
  params: ClaimAssociatedSignalEventsRequest
): string =>
  client.getUri({
    method: "get",
    url: createURL([CLAIMS_BASE_ROUTE, "associatedSignalEvents"]),
    params,
  });

export interface ClaimAssociatedVehiclesRequest
  extends APIPaginatedRequest, ListVehiclesRequest {
  claimFilter?: string;
  smartGroupType?: string;
}

export const listAssociatedVehiclesRequestURI = (
  params: ClaimAssociatedVehiclesRequest
): string =>
  client.getUri({
    method: "get",
    url: createURL([CLAIMS_BASE_ROUTE, "associatedVehicles"]),
    params,
  });

export const countAssociatedVehiclesRequestURI = (
  params: ClaimAssociatedVehiclesRequest
): string =>
  client.getUri({
    method: "get",
    url: createURL([CLAIMS_BASE_ROUTE, "associatedVehicles", "count"]),
    params,
  });

const getAssociatedVehiclesExportRequestURI = ({
  type = "xlsx",
  ...params
}: ExportRequest): string =>
  client.getUri({
    method: "get",
    url: createURL([CLAIMS_BASE_ROUTE, "associatedVehicles", "export", type]),
    params,
  });

export const getClaimsAssociatedVehiclesExport = (args: ExportRequest) =>
  getFetcher<Blob>(getAssociatedVehiclesExportRequestURI(args), {
    responseType: "blob",
  });

export const listAssociatedVehiclesTimelineRequestURI = (
  params: AssociatedVehicleTimelineRequest
): string =>
  client.getUri({
    method: "get",
    url: createURL([CLAIMS_BASE_ROUTE, "associatedVehiclesTimeline"]),
    params,
  });

export interface MatchEventsRequest {
  id: string;
  filter: string;
  groupBy?: string;
}

export interface MatchedInfo {
  matchingClaimDetails: Record<string, string[]> | null;
}

export interface MatchedSignalEventsOccurrence extends SignalEventsOccurrence {
  matchedSignalEventOccurrence?: MatchedInfo;
  groupedCount?: number;
}

export interface MatchedClaim extends Claim {
  matchedClaimDetails?: Record<string, string[]> | null;
  claimDetails: Record<string, string[]> | null;
}

export interface MatchSignalEventsOccurrencesResponse {
  matchedSignalEventOccurrences: MatchedSignalEventsOccurrence[];
  relatedSignalEventOccurrences: MatchedSignalEventsOccurrence[];
}

export interface MatchClaimsResponse {
  matchedClaims: MatchedClaim[];
  relatedClaims: MatchedClaim[];
}

export const matchSignalEventsOccurrencesRequestURI = (
  id: string,
  { ...params }: MatchEventsRequest
): string =>
  client.getUri({
    method: "GET",
    url: createURL([CLAIMS_BASE_ROUTE, id, "matchedSignalEventOccurrences"]),
    params,
  });

export const matchClaimsRequestURI = (
  id: string,
  { ...params }: MatchEventsRequest
): string =>
  client.getUri({
    method: "GET",
    url: createURL([CLAIMS_BASE_ROUTE, id, "matchedClaims"]),
    params,
  });

export interface ListClaimsSmartGroupsRequest extends ListEventsRequest {
  eventType?: string;
  chartDateField?: string;
  granularity?: string;
}

export const getDownloadAllClaimAttachmentsRequestURI = (id: string): string =>
  client.getUri({
    method: "GET",
    url: createURL([CLAIMS_BASE_ROUTE, id, "attachments", "download"]),
  });

// --------
// Claims Smart Groups Export
// --------
const getClaimsSmartGroupsExportRequestURI = ({
  type = "xlsx",
  ...params
}: ExportRequest): string =>
  client.getUri({
    method: "get",
    url: createURL([CLAIMS_BASE_ROUTE, "smartGroups", "export", type]),
    params,
  });

export const getClaimsSmartGroupsExport = (args: ExportRequest) =>
  getFetcher<Blob>(getClaimsSmartGroupsExportRequestURI(args), {
    responseType: "blob",
  });
